import React, { useEffect, useState } from 'react';
import { PageHeader, StatCard, Card, Spinner, Badge, Btn, Input, Select, useToast } from '../../components/shared/UI';
import api from '../../utils/api';

const fmtMoney = (amount, currency) =>
  `${String(currency || 'myr').toUpperCase()} ${Number(amount || 0).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
// Multi-currency totals ("MYR 1,200.00 · USD 30.00"); em-dash when nothing yet
const fmtTotals = rows => (rows?.length ? rows.map(r => fmtMoney(r.total, r.currency)).join(' · ') : '—');
const fmtDate = d => (d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');
const PLAN_LABEL = { monthly: 'Monthly', yearly: 'Yearly' };

export default function Finance() {
  const [overview, setOverview] = useState(null);
  const [list, setList] = useState(null);
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [err, setErr] = useState('');
  const [backfilling, setBackfilling] = useState(false);
  const { show, ToastContainer } = useToast();

  const loadOverview = () =>
    api.get('/admin/finance')
      .then(r => setOverview(r.data))
      .catch(e => setErr(e.response?.data?.message || e.message));
  const loadList = () =>
    api.get('/admin/finance/payments', { params: { page, limit: 25, status: status || undefined, q: q || undefined } })
      .then(r => setList(r.data))
      .catch(e => setErr(e.response?.data?.message || e.message));

  useEffect(() => { loadOverview(); }, []); // eslint-disable-line

  // Debounce the search box into the actual query
  useEffect(() => { const id = setTimeout(() => { setQ(search); setPage(1); }, 350); return () => clearTimeout(id); }, [search]);

  useEffect(() => { loadList(); }, [page, status, q]); // eslint-disable-line

  // One-off import of payments made before the ledger existed
  const backfill = async () => {
    setBackfilling(true);
    try {
      const { data } = await api.post('/stripe/backfill-payments');
      show(`Backfill done: ${data.imported} imported, ${data.alreadyRecorded} already recorded (${data.scanned} invoices scanned)`);
      await Promise.all([loadOverview(), loadList()]);
    } catch (e) {
      show(e.response?.data?.message || 'Backfill failed', 'error');
    } finally { setBackfilling(false); }
  };

  if (err) return <Card><p style={{ color: 'var(--danger)' }}>{err}</p></Card>;
  if (!overview) return <Spinner />;

  const months = overview.byMonth || [];
  // One bar per month: sum across currencies (labels still show the exact split)
  const monthKeys = [...new Set(months.map(m => m.month))];
  const monthBars = monthKeys.map(month => {
    const rows = months.filter(m => m.month === month);
    return { month, total: rows.reduce((s, r) => s + r.total, 0), label: rows.map(r => fmtMoney(r.total, r.currency)).join(' · ') };
  });
  const maxMonth = Math.max(1, ...monthBars.map(m => m.total));
  const subs = overview.subscribers || {};

  return (
    <div>
      <ToastContainer />
      <PageHeader
        title="Finance"
        subtitle="Subscription payments recorded from Stripe"
        actions={<Btn variant="outline" onClick={backfill} disabled={backfilling}>{backfilling ? 'Importing…' : '⟳ Backfill from Stripe'}</Btn>}
      />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 16, marginBottom: 24 }}>
        <StatCard label="Revenue — 30 days" value={fmtTotals(overview.revenue.last30d)} icon="💰" color="#10B981" />
        <StatCard label="Revenue — 12 months" value={fmtTotals(overview.revenue.last365d)} icon="📅" color="#2E75B6" />
        <StatCard label="Revenue — all time" value={fmtTotals(overview.revenue.allTime)} icon="🏦" color="#8B5CF6"
          sub={`${(overview.revenue.allTime || []).reduce((s, r) => s + r.count, 0)} payments`} />
        <StatCard label="Active subscribers" value={(subs.monthly || 0) + (subs.yearly || 0)} icon="👥" color="#F59E0B"
          sub={`${subs.monthly || 0} monthly · ${subs.yearly || 0} yearly`} />
        <StatCard label="Failed — 30 days" value={overview.failures?.last30d || 0} icon="⚠️"
          color={overview.failures?.last30d ? '#DC2626' : '#64748b'} sub={`${subs.pastDue || 0} past due now`} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))', gap: 16, marginBottom: 20 }}>
        {monthBars.length > 0 && (
          <Card>
            <h3 style={cardH}>Revenue — last 12 months</h3>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 110 }}>
              {monthBars.map(m => (
                <div key={m.month} title={`${m.month}: ${m.label}`}
                  style={{
                    flex: 1, minWidth: 10, borderRadius: '3px 3px 0 0',
                    height: `${Math.max(4, (m.total / maxMonth) * 100)}%`,
                    background: 'var(--primary, #2E75B6)', opacity: 0.85,
                  }} />
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text-subtle)', marginTop: 6 }}>
              <span>{monthBars[0]?.month}</span>
              <span>{monthBars[monthBars.length - 1]?.month}</span>
            </div>
          </Card>
        )}

        <Card>
          <h3 style={cardH}>Revenue by plan</h3>
          {(overview.byPlan || []).length === 0
            ? <p style={{ fontSize: 13, color: 'var(--text-subtle)' }}>No payments yet</p>
            : overview.byPlan.map(p => (
                <div key={`${p.plan}-${p.currency}`} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '6px 0' }}>
                  <span style={{ color: 'var(--text-muted)' }}>{PLAN_LABEL[p.plan] || p.plan} ({p.count})</span>
                  <span style={{ fontWeight: 600, color: 'var(--text)' }}>{fmtMoney(p.total, p.currency)}</span>
                </div>
              ))}
          {overview.lastPaymentAt && (
            <p style={{ fontSize: 12, color: 'var(--text-subtle)', marginTop: 10 }}>Last payment: {fmtDate(overview.lastPaymentAt)}</p>
          )}
        </Card>
      </div>

      <Card>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 14 }}>
          <h3 style={{ ...cardH, marginBottom: 0, paddingBottom: 0, borderBottom: 'none', flex: 1 }}>
            Payments{list ? ` (${list.total})` : ''}
          </h3>
          <Input placeholder="Search name, email, invoice…" value={search} onChange={e => setSearch(e.target.value)} style={{ width: 220 }} />
          <Select value={status} onChange={e => { setStatus(e.target.value); setPage(1); }} style={{ width: 140 }}>
            <option value="">All statuses</option>
            <option value="succeeded">Succeeded</option>
            <option value="failed">Failed</option>
          </Select>
        </div>

        {!list ? <Spinner /> : list.payments.length === 0 ? (
          <p style={{ fontSize: 13, color: 'var(--text-subtle)', padding: '16px 0' }}>No payments recorded yet. Rows appear here as Stripe charges succeed or fail.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr>
                  {['Date', 'User', 'Amount', 'Plan', 'Billing period', 'Invoice', 'Status', ''].map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: '8px 10px', borderBottom: '2px solid var(--border)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {list.payments.map(p => (
                  <tr key={p._id}>
                    <td style={cell}>{fmtDate(p.paidAt)}</td>
                    <td style={cell}>
                      <div style={{ fontWeight: 600, color: 'var(--text)' }}>{p.userName || '—'}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-subtle)' }}>{p.userEmail}</div>
                    </td>
                    <td style={{ ...cell, fontWeight: 600, whiteSpace: 'nowrap', color: p.status === 'succeeded' ? 'var(--success, #10B981)' : 'var(--danger)' }}>
                      {fmtMoney(p.amount, p.currency)}
                    </td>
                    <td style={cell}>{PLAN_LABEL[p.plan] || '—'}</td>
                    <td style={{ ...cell, whiteSpace: 'nowrap' }}>{p.periodStart ? `${fmtDate(p.periodStart)} – ${fmtDate(p.periodEnd)}` : '—'}</td>
                    <td style={cell}>{p.invoiceNumber || '—'}</td>
                    <td style={cell}>
                      <Badge label={p.status} color={p.status === 'succeeded' ? '#10B981' : '#DC2626'} />
                      {p.status === 'failed' && p.failureMessage && (
                        <div style={{ fontSize: 11, color: 'var(--text-subtle)', marginTop: 2 }}>{p.failureMessage}</div>
                      )}
                    </td>
                    <td style={cell}>
                      {p.receiptUrl && <a href={p.receiptUrl} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: 'var(--primary, #2E75B6)' }}>Receipt ↗</a>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {list && list.pages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, marginTop: 14 }}>
            <Btn variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>← Prev</Btn>
            <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>Page {list.page} of {list.pages}</span>
            <Btn variant="outline" size="sm" disabled={page >= list.pages} onClick={() => setPage(p => p + 1)}>Next →</Btn>
          </div>
        )}
      </Card>
    </div>
  );
}

const cardH = { fontSize: 14, fontWeight: 600, color: 'var(--heading)', marginBottom: 14, paddingBottom: 10, borderBottom: '1px solid var(--border-light)' };
const cell = { padding: '9px 10px', borderBottom: '1px solid var(--border-light)', color: 'var(--text)', verticalAlign: 'top' };
